import { useState, useRef } from 'react';
import { Download, Upload, AlertTriangle, Database } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function SettingsPage() {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [pendingFile, setPendingFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const fileRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/export-data');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `freecoffee-backup-${user?.community_id || 'community'}-${date}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const totalDocs = Object.values(res.data?.collections || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
      toast.success(`Exported ${totalDocs.toLocaleString()} records`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    setConfirmOpen(true);
  };

  const handleConfirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const text = await pendingFile.text();
      const body = JSON.parse(text);
      const res = await api.post(`/admin/import-data?mode=${importMode}`, body);
      setLastImport(res.data);
      const totalIn = Object.values(res.data?.imported || {}).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Imported ${totalIn.toLocaleString()} records (${importMode})`);
      setConfirmOpen(false);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-2" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Backups and community data management</p>
      </div>

      <Card className="border-slate-100 mb-6" data-testid="export-card">
        <CardHeader>
          <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
            <Download size={18} className="text-emerald-600" /> Export Full Data
          </CardTitle>
          <CardDescription className="text-xs">
            Download a JSON backup of every record for this community (units, dues, payments, transactions, expenditure, tickets, announcements, etc.). Keep this file safe — it can restore the system to today's state.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-data-btn"
          >
            {exporting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
            Download Backup
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-100 mb-6" data-testid="import-card">
        <CardHeader>
          <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
            <Upload size={18} className="text-blue-600" /> Import Backup
          </CardTitle>
          <CardDescription className="text-xs">
            Restore data from a backup file. <strong>Merge</strong> adds records from the file alongside existing data. <strong>Replace</strong> wipes all community data first — useful when restoring from scratch.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex gap-2">
              <Button
                variant={importMode === 'merge' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setImportMode('merge')}
                data-testid="import-mode-merge"
              >
                Merge
              </Button>
              <Button
                variant={importMode === 'replace' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setImportMode('replace')}
                data-testid="import-mode-replace"
                className={importMode === 'replace' ? 'bg-rose-600 hover:bg-rose-700' : ''}
              >
                Replace
              </Button>
            </div>
            {importMode === 'replace' && (
              <Badge className="bg-rose-100 text-rose-700 text-xs gap-1">
                <AlertTriangle size={12} /> Existing data will be deleted
              </Badge>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={handlePickFile}
            className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            data-testid="import-file-input"
          />
        </CardContent>
      </Card>

      {lastImport && (
        <Card className="border-emerald-100 bg-emerald-50/50" data-testid="last-import-result">
          <CardHeader>
            <CardTitle className="text-sm font-['Outfit'] flex items-center gap-2 text-emerald-700">
              <Database size={16} /> Last import
            </CardTitle>
            <CardDescription className="text-xs">
              Mode: <strong>{lastImport.mode}</strong> · From {lastImport.exported_by || 'unknown'} · Exported {lastImport.exported_at?.slice(0, 10)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {Object.entries(lastImport.imported || {}).map(([coll, n]) => (
                <div key={coll} className="flex justify-between bg-white rounded px-2 py-1 border border-emerald-100">
                  <span className="text-slate-600">{coll}</span>
                  <span className="font-semibold text-emerald-700">{n}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirm-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importMode === 'replace' ? <AlertTriangle className="text-rose-600" size={18} /> : <Upload className="text-blue-600" size={18} />}
              Confirm import
            </DialogTitle>
            <DialogDescription className="text-xs pt-2">
              {importMode === 'replace' ? (
                <span className="text-rose-700">
                  This will <strong>delete ALL current community data</strong> (units, payments, expenses, tickets, etc.) and replace it with the contents of <strong>{pendingFile?.name}</strong>. This action cannot be undone — make sure you have a fresh export first.
                </span>
              ) : (
                <span>
                  Records from <strong>{pendingFile?.name}</strong> will be added to the existing data. Duplicates may cause errors that will be skipped.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-500">
            <Label className="text-xs">File size:</Label> {pendingFile ? `${(pendingFile.size / 1024).toFixed(1)} KB` : ''}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setPendingFile(null); if (fileRef.current) fileRef.current.value = ''; }} disabled={importing}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={importing}
              className={importMode === 'replace' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}
              data-testid="confirm-import-btn"
            >
              {importing && <Loader2 size={14} className="animate-spin mr-2" />}
              {importMode === 'replace' ? 'Replace All Data' : 'Merge Backup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
